// ============================================================
// InventoryTab.jsx  —  Noble Enterprises (tape module)
//
// TWO SECTIONS (sub-tabs)
//   Raw Material  — every physical jumbo roll in stock, shown
//                   in YARDS. Filters, close/reopen a roll.
//   WIP           — cores produced (Phase-1 cut) but not yet
//                   cut to finished tape, grouped by spec.
//                   Shows produced / used / in-stock counts and
//                   the in-stock length breakdown, in yards.
//
// UNITS
//   The DB stores meters (PO/Inward are in meters). Everything
//   on this screen is shown in YARDS via utils/units.js. No
//   meters are shown here.
//
// Rolls are created by Inward. Nothing here adds one. Cores are
// created by production. Nothing here adds one. This is a view
// of stock, plus the one action that ends a roll's life.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. StateBadge + StockBar
//   C. CloseRollModal
//   D. RollDetailRow
//   E. RawMaterialSection
//   F. WipSection
//   G. InventoryTab (sub-tab shell)
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Check, Search, Archive, RotateCcw, FileText, ChevronDown as Chevron, Package, Layers } from 'lucide-react';
import WindowModal from '../components/WindowModal';
import StockReportModal from './reports/StockReportModal';
import { hasFullAccess } from '../../../utils/access';
// Values arrive from the DB already in yards — format only, no conversion.
const fmtYards = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} yards`;

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money   = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. STATE BADGE + STOCK BAR
//
// Rolls are Active or Closed — the retired "nearly done" state
// is gone (no core length on the roll any more).
// ============================================================

const STATE_STYLE = {
  active: { label: 'Active', bg: C.successBg,  fg: C.success },
  closed: { label: 'Closed', bg: C.borderSoft, fg: C.textMuted },
};

const StateBadge = ({ roll }) => {
  const s = STATE_STYLE[roll.status] || STATE_STYLE.active;
  return (
    <span style={{
      fontSize: '12px', fontWeight: '600', padding: '3px 11px',
      borderRadius: '11px', background: s.bg, color: s.fg,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

// Bar shows what's LEFT, not what's used — longer is better,
// which reads the right way at a glance.
const StockBar = ({ remaining, total, status }) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0;
  const colour = status === 'closed' ? C.textMuted : C.success;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        flex: 1, minWidth: '60px', height: '6px', borderRadius: '3px',
        background: C.borderSoft, overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: colour }} />
      </div>
      <span style={{
        fontSize: '12px', color: C.textMuted, minWidth: '34px', textAlign: 'right',
      }}>
        {Math.round(pct)}%
      </span>
    </div>
  );
};


// ============================================================
// C. CLOSE ROLL MODAL
//
// The scrap field is what makes closing honest. It reconciles
// what the books say is left against what's physically on the
// floor, and records the gap instead of letting it disappear.
// ============================================================

const CloseRollModal = ({ roll, onClose, onDone, notify }) => {
  const [scrapKg, setScrapKg] = useState('');
  const [saving, setSaving]   = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const bookRemaining = Number(roll.remaining_yards);

  const save = async () => {
    if (scrapKg !== '' && Number(scrapKg) < 0) {
      return notify('error', 'Scrap weight cannot be negative');
    }
    setSaving(true);
    try {
      await axios.put(`${API}/tape/jumbo-rolls/${roll.id}/close`,
        { closing_scrap_kg: scrapKg }, { headers });
      notify('success', `Roll ${roll.vendor_roll_number} closed`);
      onDone();
      onClose();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not close this roll');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WindowModal
      windowId="close-roll"
      title={`Close roll — ${roll.vendor_roll_number}`}
      onClose={onClose}
      width={520}
    >
      <div style={{
        background: C.rowAlt, borderRadius: '8px', padding: '14px 16px',
        marginBottom: '18px', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px',
      }}>
        {[
                ['Received', fmtYards(roll.total_yards)],
          ['Consumed', fmtYards(roll.consumed_yards)],
          ['Book remaining', fmtYards(bookRemaining)],
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <Field label="Actual scrap left (kg)">
        <TextInput
          type="number" step="0.01" min="0"
          value={scrapKg}
          onChange={e => setScrapKg(e.target.value)}
          placeholder="0"
          autoFocus
        />
      </Field>

      <p style={{ fontSize: '12px', color: C.textMuted, margin: '8px 0 0', lineHeight: 1.5 }}>
        Weigh what's physically left of this roll. The books still show
                    {' '}<strong style={{ color: C.text }}>{fmtYards(bookRemaining)}</strong>{' '}
        unaccounted for — closing records that as loss rather than leaving
        it open forever.
      </p>

      <div style={{
        marginTop: '18px', padding: '11px 14px', borderRadius: '8px',
        background: C.warningBg, color: '#78350f', fontSize: '13px', lineHeight: 1.5,
      }}>
        Once closed, this roll is final and it disappears from production.
        Only the owner can reopen it.
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
        <Btn onClick={save} disabled={saving}>
          <Check size={16} /> {saving ? 'Closing…' : 'Close roll'}
        </Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// D. ROLL DETAIL ROW
//
// Reads the roll's OWN frozen spec columns. The retired
// catalogue joins (category, quality, width_inches, core
// length, expected cores) are gone.
// ============================================================

const RollDetailRow = ({ roll, colSpan }) => {
  const spec   = Number(roll.spec_yards);
  const actual = Number(roll.total_yards);
  const isShort = spec > 0 && actual < spec;

  const box = (label, value, colour = C.text) => (
    <div key={label} style={{
      background: '#ffffff', border: `1px solid ${C.border}`,
      borderRadius: '8px', padding: '12px 14px',
    }}>
      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '15px', fontWeight: '700', color: colour, marginTop: '3px' }}>
        {value}
      </div>
    </div>
  );

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: '18px 22px', background: C.rowAlt,
        borderTop: `1px solid ${C.borderSoft}`,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '12px', marginBottom: '14px',
        }}>
          {box('Tape type', roll.tape_type_name)}
          {box('Micron', `${roll.micron} µ`)}
          {box('Width', `${roll.width_mm} mm`)}
                    {box('Spec length', fmtYards(roll.spec_yards))}
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
        }}>
          {box('Vendor', roll.vendor_name)}
          {box('Purchase order', roll.po_number)}
          {box('Bill number', roll.bill_number)}
          {box('Received', fmtDate(roll.received_date))}
                    {hasFullAccess('adhesive')
            && box('Roll cost', money(roll.rate))}
          {roll.status === 'closed' && box('Closing scrap', `${Number(roll.closing_scrap_kg || 0)} kg`)}
        </div>

        {/* A short roll arrived under its spec. Worth surfacing. */}
        {isShort && (
          <div style={{
            marginTop: '14px', padding: '11px 14px', borderRadius: '8px',
            background: C.warningBg, color: '#78350f', fontSize: '13px',
          }}>
                        This roll arrived at {fmtYards(actual)} against a spec of {fmtYards(spec)}.
          </div>
        )}
      </td>
    </tr>
  );
};


// ============================================================
// E. RAW MATERIAL SECTION
// The jumbo roll stock table. Everything shown in yards.
// ============================================================

const RawMaterialSection = ({ notify }) => {
  const [rolls, setRolls]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [tapeTypes, setTapeTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [closingRoll, setClosingRoll] = useState(null);
  const [showStockReport, setShowStockReport] = useState(false);

    const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter]     = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [search, setSearch]             = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const canReopen = hasFullAccess('adhesive');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter)   params.append('tape_type_id', typeFilter);
      if (vendorFilter) params.append('vendor_id', vendorFilter);
      if (search.trim()) params.append('search', search.trim());

      const [r, s, t, v] = await Promise.all([
        axios.get(`${API}/tape/jumbo-rolls?${params.toString()}`, { headers }),
        axios.get(`${API}/tape/jumbo-rolls/summary`, { headers }),
        axios.get(`${API}/tape/settings/tape-types`, { headers }),
        axios.get(`${API}/tape/vendors/active`, { headers }),
      ]);
                // Display only — newest received first, then newest entry.
          // The backend order (FIFO for production) is untouched.
          const sorted = [...(r.data.data || [])].sort((a, b) =>
            (new Date(b.received_date) - new Date(a.received_date)) || (b.id - a.id)
          );
          setRolls(sorted);
      setSummary(s.data.data || null);
      setTapeTypes(t.data.data || []);
      setVendors(v.data.data || []);
    } catch {
      notify('error', 'Could not load inventory. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, vendorFilter, search]); // eslint-disable-line

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const reopen = async (roll) => {
    if (!window.confirm(`Reopen roll ${roll.vendor_roll_number}?`)) return;
    try {
      await axios.put(`${API}/tape/jumbo-rolls/${roll.id}/reopen`, {}, { headers });
      notify('success', 'Roll reopened');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not reopen');
    }
  };

  return (
    <div>
      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Active rolls',    summary?.active_rolls ?? '—', C.text],
                    ['Stock in hand',   summary ? fmtYards(summary.yards_in_stock) : '—', C.text],
          ['Stock value',     summary ? money(summary.stock_value) : '—', C.text],
          ['Closed rolls',    summary?.closed_rolls ?? '—', C.textMuted],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        {/* Header + filters */}
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}` }}>
          <div style={{
            marginBottom: '14px', display: 'flex',
            alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
                Jumbo roll stock
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
                              Click a roll to see its spec and where it came from. Newest first.
              </p>
            </div>

                        {canReopen && (
              <Btn variant="ghost" onClick={() => setShowStockReport(true)}>
                <FileText size={16} /> Stock Report
              </Btn>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px' }}>
              <Search
                size={15}
                style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }}
              />
              <input
                style={{ ...inputStyle, paddingLeft: '34px' }}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search roll number…"
              />
            </div>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All States</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '140px' }}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              {tapeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <select
              style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}
              value={vendorFilter}
              onChange={e => setVendorFilter(e.target.value)}
            >
              <option value="">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Roll number</th>
                <th style={thStyle}>Spec</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Received</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Remaining</th>
                <th style={{ ...thStyle, minWidth: '130px' }}>Left</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>State</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && rolls.map(roll => {
                const isOpen = expandedId === roll.id;

                return (
                  <React.Fragment key={roll.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : roll.id)}
                      style={{
                        cursor: 'pointer',
                        background: isOpen ? C.rowAlt : 'transparent',
                        opacity: roll.status === 'closed' ? 0.65 : 1,
                      }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {roll.vendor_roll_number || '—'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: '13px' }}>{roll.jumbo_name}</div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                                                {fmtYards(roll.total_yards)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                                                {fmtYards(roll.remaining_yards)}
                      </td>
                      <td style={tdStyle}>
                        <StockBar
                                                    remaining={Number(roll.remaining_yards)}
                          total={Number(roll.total_yards)}
                          status={roll.status}
                        />
                      </td>
                      <td style={{ ...tdStyle, fontSize: '13px' }}>{roll.vendor_name}</td>
                      <td style={tdStyle}><StateBadge roll={roll} /></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {roll.status === 'active' && (
                          <IconBtn
                            title="Close roll"
                            onClick={(e) => { e.stopPropagation(); setClosingRoll(roll); }}
                          >
                            <Archive size={15} />
                          </IconBtn>
                        )}
                        {roll.status === 'closed' && canReopen && (
                          <IconBtn
                            title="Reopen roll"
                            onClick={(e) => { e.stopPropagation(); reopen(roll); }}
                          >
                            <RotateCcw size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && <RollDetailRow roll={roll} colSpan={8} />}
                  </React.Fragment>
                );
              })}

              {!loading && rolls.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No rolls match. Receive goods against a purchase order to build stock.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showStockReport && (
        <StockReportModal onClose={() => setShowStockReport(false)} />
      )}

      {closingRoll && (
        <CloseRollModal
          roll={closingRoll}
          onClose={() => setClosingRoll(null)}
          onDone={load}
          notify={notify}
        />
      )}
    </div>
  );
};


// ============================================================
// F. WIP SECTION
//
// Cores produced but not yet cut to finished tape, grouped by
// spec. Each group: produced / used / in-stock counts, plus
// the in-stock length breakdown (lengths already in yards from
// the DB — no conversion, cores are stored in yards).
// ============================================================

const WipSection = ({ notify }) => {
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tape/jumbo-rolls/wip`, { headers });
      setGroups(res.data.data || []);
    } catch {
      notify('error', 'Could not load WIP cores.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const totalInStock = groups.reduce((sum, g) => sum + Number(g.in_stock || 0), 0);
  const totalProduced = groups.reduce((sum, g) => sum + Number(g.produced || 0), 0);
  const totalUsed = groups.reduce((sum, g) => sum + Number(g.used || 0), 0);

  const stat = (label, value, colour = C.text) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: '18px', fontWeight: '700', color: colour }}>{value}</span>
    </div>
  );

  return (
    <div>
      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Cores in stock', loading ? '—' : totalInStock, C.text],
          ['Total produced', loading ? '—' : totalProduced, C.textSoft],
          ['Total used',     loading ? '—' : totalUsed, C.textMuted],
          ['Spec groups',    loading ? '—' : groups.length, C.textMuted],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
          padding: '40px', textAlign: 'center', color: C.textMuted,
        }}>
          Loading…
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
          padding: '48px', textAlign: 'center', color: C.textMuted,
        }}>
          No cores in progress. Cores appear here once production cuts them from a jumbo roll.
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px',
        }}>
          {groups.map((g, idx) => (
            <div key={idx} style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '18px 20px',
            }}>
              {/* Spec label */}
              <div style={{
                fontSize: '15px', fontWeight: '700', color: C.text,
                marginBottom: '14px', paddingBottom: '12px',
                borderBottom: `1px solid ${C.borderSoft}`,
              }}>
                {g.spec_label}
              </div>

              {/* Counts */}
              <div style={{
                display: 'flex', gap: '22px', marginBottom: '16px',
              }}>
                {stat('In stock', g.in_stock, C.success)}
                {stat('Produced', g.produced)}
                {stat('Used', g.used, C.textMuted)}
              </div>

              {/* In-stock length breakdown */}
              <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
                In-stock lengths
              </div>
              {g.lengths && g.lengths.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {g.lengths.map((l, i) => (
                    <span key={i} style={{
                      fontSize: '13px', fontWeight: '600', color: C.text,
                      background: C.rowAlt, border: `1px solid ${C.border}`,
                      borderRadius: '8px', padding: '5px 11px',
                    }}>
                      {Number(l.length_yards).toLocaleString('en-PK')} yards
                      <span style={{ color: C.textMuted, fontWeight: '500' }}> × {l.qty}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: C.textMuted }}>None in stock</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ============================================================
// G. INVENTORY TAB — sub-tab shell
// ============================================================

const InventoryTab = () => {
  const [section, setSection] = useState('raw'); // 'raw' | 'wip'
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

    const tab = (id, label, Icon, activeBg, activeBorder, idleColor) => {
    const active = section === id;
    return (
      <button
        onClick={() => setSection(id)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          padding: '14px 28px', borderRadius: '11px', fontSize: '16px',
          fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
          border: `2px solid ${active ? activeBorder : C.border}`,
          background: active ? activeBg : '#ffffff',
          color: active ? '#ffffff' : idleColor,
          boxShadow: active ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        <Icon size={20} /> {label}
      </button>
    );
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {tab('raw', 'Raw Material', Package, C.brand, C.brand, C.brand)}
        {tab('wip', 'WIP', Layers, C.info, C.info, C.info)}
      </div>

      {section === 'raw' && <RawMaterialSection notify={notify} />}
      {section === 'wip' && <WipSection notify={notify} />}
    </div>
  );
};

export default InventoryTab;